import { useCallback } from 'react';
import {
  useGetCommentsQuery,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
} from '../api/commentsApi';
import { normalizeMediaBeforeSave } from '../../detail-form/richTextMediaHelpers';

export const useComments = (entityId: number, entityTypeId: number) => {
  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetCommentsQuery({ entityId, entityTypeId }, { skip: !entityId || !entityTypeId });

  const [addCommentMutation, { isLoading: isAdding }] = useAddCommentMutation();
  const [editCommentMutation, { isLoading: isEditing }] = useEditCommentMutation();
  const [deleteCommentMutation, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const comments = response || [];

  const addComment = useCallback(
    async (content: string, tags?: number[]) => {
      try {
        await addCommentMutation({
          EntityId: entityId,
          EntityTypeId: entityTypeId,
          CommentContent: normalizeMediaBeforeSave(content),
          TaggedUsersId: tags,
        }).unwrap();
      } catch (err) {
        console.error('Failed to add comment', err);
        throw err;
      }
    },
    [addCommentMutation, entityId, entityTypeId]
  );

  const editComment = useCallback(
    async (id: number, content: string, tags?: number[]) => {
      try {
        await editCommentMutation({
          CommentId: id,
          EntityId: entityId,
          EntityTypeId: entityTypeId,
          CommentContent: normalizeMediaBeforeSave(content),
          TaggedUsersId: tags,
        }).unwrap();
      } catch (err) {
        console.error('Failed to edit comment', err);
        throw err;
      }
    },
    [editCommentMutation, entityId, entityTypeId]
  );

  const deleteComment = useCallback(
    async (id: number) => {
      try {
        await deleteCommentMutation(id).unwrap();
      } catch (err) {
        console.error('Failed to delete comment', err);
        throw err;
      }
    },
    [deleteCommentMutation]
  );

  return {
    comments,
    isLoading,
    isError,
    error,
    isAdding,
    isEditing,
    isDeleting,
    refetch,
    addComment,
    editComment,
    deleteComment,
  };
};
